import {
  BOLDSIGN_EMBED_ORIGIN,
  BoldSignEmbedEvent,
  SigningOutcome,
} from 'utils/signing/constants';

const OVERLAY_Z_INDEX = 9999;

/**
 * Opens a BoldSign embedded signing session and resolves once it ends.
 *
 * BoldSign ships no embed SDK, so the iframe and its lifecycle are ours to
 *   manage: we mount a full-screen overlay, listen for the provider's
 *   `postMessage` events, and expose an explicit close affordance because
 *   BoldSign — unlike HelloSign — emits no close/cancel event.
 *
 * @see https://developers.boldsign.com/embedded-signing/embed-in-an-iFrame/
 */
export const openBoldSignEmbed = (signUrl: string): Promise<SigningOutcome> =>
  new Promise((resolve, reject) => {
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Document signing');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: String(OVERLAY_Z_INDEX),
    });

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.textContent = 'Close';
    closeButton.setAttribute('aria-label', 'Close document signing');
    Object.assign(closeButton.style, {
      alignSelf: 'flex-end',
      margin: '12px',
      padding: '8px 16px',
      border: 'none',
      borderRadius: '4px',
      backgroundColor: '#fff',
      fontSize: '16px',
      cursor: 'pointer',
    });

    const iframe = document.createElement('iframe');
    iframe.src = signUrl;
    iframe.title = 'Document signing';
    Object.assign(iframe.style, {
      flex: '1',
      width: '100%',
      border: 'none',
    });

    let isSettled = false;

    const teardown = () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('keydown', handleKeyDown);
      overlay.remove();
    };

    /**
     * Guards against a late event settling an already-finished session — e.g.
     *   BoldSign posting `onDocumentSigned` right as the signer hits Close.
     */
    const settle = (finish: () => void) => {
      if (isSettled) return;
      isSettled = true;
      teardown();
      finish();
    };

    const cancel = () => settle(() => resolve(SigningOutcome.Cancelled));

    function handleMessage(event: MessageEvent) {
      if (event.origin !== BOLDSIGN_EMBED_ORIGIN) return;

      switch (event.data?.action) {
        case BoldSignEmbedEvent.DocumentSigned:
          settle(() => resolve(SigningOutcome.Signed));
          break;
        // Declining and reassigning both end the session without a signature
        //   from this signer, which is the same outcome as closing the overlay.
        case BoldSignEmbedEvent.DocumentDeclined:
        case BoldSignEmbedEvent.DocumentReassigned:
          cancel();
          break;
        case BoldSignEmbedEvent.DocumentSigningFailed:
        case BoldSignEmbedEvent.DocumentDecliningFailed:
        case BoldSignEmbedEvent.DocumentReassigningFailed:
          settle(() => reject(new Error('BoldSign embedded signing failed')));
          break;
        default:
          break;
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') cancel();
    }

    closeButton.addEventListener('click', cancel);
    window.addEventListener('message', handleMessage);
    document.addEventListener('keydown', handleKeyDown);

    overlay.appendChild(closeButton);
    overlay.appendChild(iframe);
    document.body.appendChild(overlay);
  });
